import React, {FunctionComponent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import PartnerDataGrid, {DataGridColumn, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, DataGridRow, useDataGridSavedState} from "../../../../../../containers/data-grid";
import BizProductionApiService from "../../../../../../../core/services/api_service/production_api_service";
import useSearch from "../../../../../../hooks/use-search";
import Utils from "../../../../../../../core/services/utils/utils";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import DateUtils from "../../../../../../../core/services/date-utils";
import {FormApi} from "final-form";
import Grid2 from "@mui/material/Unstable_Grid2";
import ToggleButtons from "../../../../../../components/base/button/toggle_buttons";
import axios from "axios";


interface WarrantyRow {
    reason?: string;
    count?: number;
    _id?: string;
}

export interface PurchasePlanningInformationWarrantiesDialogProps {
    open: boolean;
    close: () => void;
    partNumber?: string;
    vendorCode?: string;
}

const dataGridColumnNames = {
    reason: "reason",
    submittedClaims: "submittedClaims",
}

const PurchasePlanningDetailsWarrantiesDialog: FunctionComponent<PurchasePlanningInformationWarrantiesDialogProps> = (props) => {
    const {close} = props;
    const [rows, setRows] = useState<WarrantyRow[]>([]);
    const isMounted = useIsMounted();

    const dataGridSaveConfig = useMemo(() => ({name: `PurchasePlanning_Warranties_DG`, version: 1}), [])

    const columns = useMemo<DataGridColumn[]>(() => [
        {
            name: dataGridColumnNames.reason,
            title: "Reason",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            width: {type: DataGridColumnWidthTypes.flex, size: 4},
        },
        {
            name: dataGridColumnNames.submittedClaims,
            title: "Submitted Claims",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: {type: DataGridColumnWidthTypes.flex, size: 1},
        },
    ] as DataGridColumn[], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    })

    /**
     * Searches for tags in the system:
     * - Calls the api regarding this action
     * - if the api call was successful, updates the pagination information based on the received value
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args) => {
        if (!props.vendorCode || !props.partNumber) {
            setRows([]);
            return;
        }
        const response = await BizProductionApiService.v1.purchasePlanning.getWarrantyReturnDetails({
            startDateTime: args.filters?.startDate,
            endDateTime: args.filters?.endDate,
            partNumber: props.partNumber ?? "",
            vendorCode: props.vendorCode ?? "",
        }, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRows(response.data?.map(e => ({
                ...e,
                _id: Utils.createUUID(),
            })) ?? []);
        }
    }, [isMounted, props.partNumber, props.vendorCode]);

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({}), []);

    const {filters, setFilters, loading, performSearch} = useSearch(search, initialSearchDataSettings)

    /**
     * Resets the import state when the dialog is opened.
     */
    useEffect(() => {
        if (props.open) {
            setRows([]);
            const cancelTokenSource = axios.CancelToken.source();
            void performSearch(cancelTokenSource);
            return () => {
                cancelTokenSource.cancel();
            }
        }
    }, [performSearch, props.open]);

    const dataGridRows = useMemo<DataGridRow[]>(() => {
        return rows.map<DataGridRow>((row) => {
            return ({
                key: row?._id ?? "",
                data: row,
                cells: {
                    [dataGridColumnNames.reason]: row.reason ?? ' ',
                    [dataGridColumnNames.submittedClaims]: row.count?.toString() ?? '',
                }
            })
        });
    }, [rows]);

    return (
        <>
            <Dialog open={props.open} onClose={close} maxWidth={'mdlg'} fullWidth>
                <DialogTitle fontWeight={600}>
                    Warranty and Return Breakdown for {props.partNumber ?? ""} - {props.vendorCode ?? ""}
                </DialogTitle>
                <DialogContent>
                    <SearchSection setFilters={setFilters} filters={filters}/>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns: columns}}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        visibleRows={7}
                        hideFooter
                        hideExporting
                        state={{
                            pagination: {currentPage: 1, pageSize: rows.length, length: rows.length},
                            loading: {state: loading},
                            density: dataGridDensity,
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', p: 0, m: 0}}>
                    <Button
                        variant={'dialog'}
                        size={'medium'}
                        type={'button'}
                        color={'primary'}
                        borderRadius={'0 8px 0 8px'}
                        onClick={() => close()}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}


const formKeys = {
    startDate: "startDate",
    endDate: "endDate",
}

const schema = Yup.object().shape({
    [formKeys.startDate]: (Yup.mixed() as any)
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formKeys.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formKeys.endDate]: (Yup.mixed() as any)
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formKeys.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});


const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const DateOptionsValues = {
    last30Days: 'last30Days',
    last90Days: 'last90Days',
    lastYear: 'lastYear',
    last2Years: 'last2Years',
}

const DateOptionRanges = {
    last30Days: () => ({
        startDate: DateUtils.UTCNow().subtract(30, 'day'),
        endDate: DateUtils.UTCNow().subtract(1, 'day'),
    }),
    last90Days: () => ({
        startDate: DateUtils.UTCNow().subtract(90, 'day'),
        endDate: DateUtils.UTCNow().subtract(1, 'day'),
    }),
    lastYear: () => ({
        startDate: DateUtils.UTCNow().subtract(1, 'year'),
        endDate: DateUtils.UTCNow().subtract(1, 'day'),
    }),
    last2Years: () => ({
        startDate: DateUtils.UTCNow().subtract(2, 'year'),
        endDate: DateUtils.UTCNow().subtract(1, 'day'),
    }),
}

const SearchSection: FunctionComponent<{ setFilters?: Function, filters: any }> = ({setFilters, filters}) => {
    const [dateOption, setDateOption] = useState();
    const formApiRef = useRef<FormApi>();

    const initialValues = useMemo(() => ({
        ...filters,
        startDate: filters?.startDate ? DateUtils.serverToUTC(filters?.startDate) : null,
        endDate: filters?.endDate ? DateUtils.serverToUTC(filters?.endDate) : null,
    }), [filters])

    /**
     * Sets the date option based on the filters
     */
    useEffect(() => {
        const period = {
            startDate: filters?.startDate ? DateUtils.serverToUTC(filters?.startDate) : null,
            endDate: filters?.endDate ? DateUtils.serverToUTC(filters?.endDate) : null,
        }
        const ranges = Object.entries(DateOptionRanges).map(e => ({key: e[0], range: e[1]()}));
        const found = ranges.find(e => e.range.startDate.isSame(period.startDate, 'minute') && e.range.endDate.isSame(period.endDate, 'minute'));
        if (found) {
            setDateOption(DateOptionsValues[found.key]);
        }
    }, [filters?.startDate, filters?.endDate])


    /**
     * Changes the query of the url to the query containing the searched values so that the list pages of orders can
     * use them and search related orders.
     * @param values {any} form values
     */
    const onSubmit = useCallback((values) => {
        setFilters?.({
            ...values,
            [formKeys.startDate]: values[formKeys.startDate] ? DateUtils.UTCToServer(values[formKeys.startDate]) : undefined,
            [formKeys.endDate]: values[formKeys.endDate] ? DateUtils.UTCToServer(values[formKeys.endDate]) : undefined,
        });
    }, [setFilters])

    /**
     * Changes the date input values based on the selected toggle button option
     */
    const changeDateByToggleButtons = useCallback((value) => {
        // refresh the same value if clicked again.
        if (!value) {
            value = dateOption;
        }
        setDateOption(value);
        let period = DateOptionRanges[value]?.();
        if (!period)
            return;
        formApiRef.current?.change('startDate', period.startDate);
        formApiRef.current?.change('endDate', period.endDate);
    }, [dateOption])


    const predefinedDateOptions = useMemo(() => [
        {
            value: DateOptionsValues.last30Days,
            name: "Last 30 days",
            disabled: false,
        },
        {
            value: DateOptionsValues.last90Days,
            name: "Last 90 days",
            disabled: false,
        },
        {
            value: DateOptionsValues.lastYear,
            name: "Last Year",
            disabled: false,
        },
        {
            value: DateOptionsValues.last2Years,
            name: "Last 2 Years",
            disabled: false,
        },
    ], [])

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
        >
            {({form, values}) => {
                formApiRef.current = form;
                return (
                    <Grid2 spacing={2} container>
                        <Grid2 xs={12} className={'toggle-buttons'}>
                            <ToggleButtons
                                value={dateOption}
                                onChange={(_, v) => changeDateByToggleButtons(v)}
                                options={predefinedDateOptions}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={6}>
                            <MuiRFF.DatePicker
                                name={'startDate'}
                                label={"Start Date"}
                                required={required.startDate}
                                maxDate={values.endDate}
                                disableFuture
                                // @ts-ignore
                                onChange={(e) => setDateOption(null) || form.change('startDate', e)}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={6}>
                            <MuiRFF.DatePicker
                                name={'endDate'}
                                label={"End Date"}
                                required
                                minDate={values.startDate}
                                disableFuture
                                // @ts-ignore
                                onChange={(e) => setDateOption(null) || form.change('endDate', e)}
                            />
                        </Grid2>
                        <Grid2 xs={12} sx={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}>
                            <Button type={'submit'} size={'large'} sx={{minWidth: 200}}>
                                Search
                            </Button>
                        </Grid2>
                    </Grid2>
                );
            }}
        </Form>
    );
}

export default PurchasePlanningDetailsWarrantiesDialog;